import React from "react";
import { Autocomplete, Box, Checkbox, Chip, TextField } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const defaultGetOptionLabel = (option) => {
  if (typeof option === "string") {
    return option;
  }

  return option?.label || "";
};

const defaultIsOptionEqualToValue = (option, value) => option?.id === value?.id;

export default function MultiSelectDropdown({
  id,
  label,
  options = [],
  value = [],
  onChange,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  closeOnSelect = false,
  getOptionLabel = defaultGetOptionLabel,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
}) {
  const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const selectedCount = value.length;
  const inputPlaceholder = selectedCount ? "" : placeholder;

  return (
    <Autocomplete
      id={id}
      multiple
      fullWidth
      disableCloseOnSelect={!closeOnSelect}
      filterSelectedOptions={false}
      options={options}
      value={value}
      disabled={disabled}
      onChange={(event, newValue) => onChange?.(newValue)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            checked={selected}
            icon={uncheckedIcon}
            checkedIcon={checkedIcon}
            sx={{
              marginRight: 1,
              color: "#d8c8f4",
              "&.Mui-checked": {
                color: "#d8c8f4",
              },
            }}
          />
          {getOptionLabel(option)}
        </li>
      )}
      renderTags={(tagValue) => {
        if (!tagValue.length) {
          return null;
        }

        const firstLabel = getOptionLabel(tagValue[0]);
        const remainingCount = tagValue.length - 1;

        return (
          <Box
            sx={{
              maxWidth: "calc(100% - 40px)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
            title={selectedCount > 1 ? `${selectedCount} selected` : firstLabel}
          >
            <Chip
              size="small"
              label={firstLabel}
              sx={{
                maxWidth: remainingCount > 0 ? "calc(100% - 42px)" : "100%",
                backgroundColor: "rgba(75, 68, 91, 0.16)",
                color: "#4b445b",
                "& .MuiChip-label": {
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
            {remainingCount > 0 && (
              <Chip
                size="small"
                label={`+${remainingCount}`}
                sx={{
                  flexShrink: 0,
                  backgroundColor: "rgba(214, 190, 0, 0.25)",
                  color: "#4b445b",
                }}
              />
            )}
          </Box>
        );
      }}
      sx={{
        width: "100%",
        "& .MuiAutocomplete-inputRoot": {
          flexWrap: "nowrap",
          minHeight: "56px",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          margin="dense"
          label={label}
          placeholder={inputPlaceholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
